// Public OTE (MOM Overtime Exemption) questionnaire (/ote-form/:token) — sent by the office, or by the yearly renewal
// reminder. Existing client: company / UEN / address and LAST YEAR'S endorsed activities pre-filled (editable).
// Shows MOM's Annex A word for word and needs the client's declaration. Submit (once) → draft OTE quotation via the
// ote-public function, which also tells the office.
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { FirmHeader } from '../components/PublicComponents'
import { oneLine } from '../lib/oneLine'
import { toast } from '../lib/toast'
import { OTE_ANNEX_A, OTE_DECLARATION } from '../lib/oteQuote'

const box = { width: '100%', padding: '9px 11px', border: '0.5px solid rgba(0,0,0,0.22)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' }
const bad = { borderColor: '#c0392b', boxShadow: '0 0 0 1px #c0392b', background: '#fdf1f0' }
const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>{children}{required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}</label>
)
const Centered = ({ icon, title, text }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
    <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 460, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>{icon}</div>
      <h2 style={{ fontWeight: 500, marginBottom: 8 }}>{title}</h2>
      <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>{text}</p>
    </div>
  </div>
)
const emptyRow = () => ({ occupation: '', department: '', no_affected: '', work_activities: '' })
const isEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s || '').trim())

export default function OTEForm() {
  const { token } = useParams()
  const [state, setState] = useState('loading')   // loading | ok | invalid | used | done
  const [f, setF] = useState({})
  const [rows, setRows] = useState([emptyRow()])
  const [copied, setCopied] = useState(false)      // rows pre-filled from last year's endorsement
  const [declared, setDeclared] = useState(false)
  const [later, setLater] = useState(false)        // "I'll send the list of work activities later"
  const [showAnnex, setShowAnnex] = useState(false)
  const [missing, setMissing] = useState({})       // field key → true (marked red)
  const [submitting, setSubmitting] = useState(false)
  const refs = useRef({})

  useEffect(() => {
    supabase.rpc('get_ote_form', { p_token: token }).then(({ data, error }) => {
      if (error || !data) { setState('invalid'); return }
      if (data.status !== 'ok') { setState(data.status === 'used' ? 'used' : 'invalid'); return }
      setF({ company_name: data.company_name || '', uen: data.uen || '', company_address: oneLine(data.company_address || ''),
        business_nature: '', pic_name: '', pic_email: '', pic_phone: '', remarks: '' })
      const last = Array.isArray(data.last_year_rows) ? data.last_year_rows : []
      if (last.length) {
        setRows(last.map(r => ({ occupation: r.occupation || '', department: r.department || '', no_affected: r.no_affected || '', work_activities: r.work_activities || '' })))
        setCopied(true)
      }
      setState('ok')
    })
  }, [token])

  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); if (missing[k]) setMissing(m => ({ ...m, [k]: false })) }
  const setRow = (i, k, v) => { setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r)); if (missing.rows) setMissing(m => ({ ...m, rows: false })) }

  async function submit() {
    const req = [['company_name', 'Company full name'], ['uen', 'UEN'], ['company_address', 'Company address'],
      ['business_nature', 'Business nature'], ['pic_name', 'Contact person'], ['pic_email', 'Contact email'], ['pic_phone', 'Contact number']]
    const miss = {}, names = []
    req.forEach(([k, l]) => { if (!String(f[k] || '').trim()) { miss[k] = true; names.push(l) } })
    if (String(f.pic_email || '').trim() && !isEmail(f.pic_email)) { miss.pic_email = true; names.push('A valid contact email') }
    const filled = rows.filter(r => String(r.work_activities || '').trim())
    if (!filled.length && !later) { miss.rows = true; names.push('At least one work activity (or tick that you will send the list later)') }
    if (!declared) { miss.declared = true; names.push('The Annex A declaration') }
    if (names.length) {
      setMissing(miss)
      toast('Please complete the missing fields', 'warning', { detail: '• ' + names.join('\n• ') })
      const first = Object.keys(miss)[0]
      try { refs.current[first]?.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch (e) {}
      return
    }
    setSubmitting(true)
    const payload = { ...f, company_address: oneLine(f.company_address).trim(), annex_a_declared: true,
      ote_rows: filled.map(r => ({ ...r, work_activities: r.work_activities.trim() })), copied_last_year: copied,
      ...(later && !filled.length ? { activities_later: true } : {}) }
    try {
      const { data, error } = await supabase.functions.invoke('ote-public', { body: { action: 'submit', token, payload } })
      if (error || data?.error) throw new Error(data?.error || error?.message || 'Please try again')
      setState('done')
    } catch (e) {
      const msg = String(e.message || e)
      if (/already been submitted/i.test(msg)) { setState('used'); return }
      toast("We couldn't submit the form", 'error', { detail: msg })
    }
    setSubmitting(false)
  }

  if (state === 'loading') return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', color: '#999' }}>Loading…</div>
  if (state === 'invalid') return <Centered icon="🔗" title="Invalid link" text="This link is not valid. Please reply to our email or contact us at (65) 6376 0550." />
  if (state === 'used') return <Centered icon="✓" title="Already submitted" text="This form has already been submitted. We will be in touch with you shortly." />
  if (state === 'done') return <Centered icon="✓" title="Thank you" text={`We have received your details for ${f.company_name}. We will prepare your quotation and send it to ${f.pic_email}.`} />

  const field = (k, label, opts = {}) => (
    <div style={{ marginBottom: 16 }} ref={el => { refs.current[k] = el }}>
      <Label required={opts.required}>{label}</Label>
      {opts.textarea
        ? <textarea value={f[k] || ''} onChange={e => set(k, e.target.value)} style={{ ...box, minHeight: 70, resize: 'vertical', ...(missing[k] ? bad : {}) }} />
        : <input type={opts.type || 'text'} value={f[k] || ''} onChange={e => set(k, opts.oneLine ? oneLine(e.target.value) : e.target.value)} style={{ ...box, ...(missing[k] ? bad : {}) }} />}
    </div>
  )
  const sectionTitle = (t) => <div style={{ fontWeight: 600, fontSize: 15, margin: '8px 0 12px' }}>{t}</div>

  return (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 16px 60px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <FirmHeader />
        <h1 style={{ fontWeight: 500, fontSize: 20, marginBottom: 8 }}>Overtime Exemption (OTE) — your details</h1>
        <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          Please fill in your company details and the work activities for which you are seeking overtime exemption. We will then prepare your quotation.
        </p>
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 22px', border: '0.5px solid rgba(0,0,0,0.1)' }}>
          {sectionTitle('Company')}
          {field('company_name', 'Company full name', { required: true })}
          {field('uen', 'UEN', { required: true })}
          {field('company_address', 'Company address', { required: true, oneLine: true })}
          {field('business_nature', 'Business nature', { required: true, textarea: true })}

          {sectionTitle('Contact person')}
          {field('pic_name', 'Name', { required: true })}
          {field('pic_email', 'Email', { required: true, type: 'email' })}
          {field('pic_phone', 'Contact number', { required: true })}

          <div ref={el => { refs.current.rows = el }}>
            {sectionTitle('Work activities for overtime exemption')}
            {copied && (
              <div style={{ fontSize: 13, color: '#27500a', background: '#eaf3de', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>
                We have filled in last year’s endorsed activities. Please check them, and change, add or remove rows as needed.
              </div>
            )}
            {missing.rows && <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 8 }}>Please list at least one work activity, or tick the box below.</div>}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: '#f7f6f3', fontSize: 14 }}>
              <input type="checkbox" checked={later} onChange={e => { setLater(e.target.checked); setMissing(m => ({ ...m, rows: false })) }}
                style={{ marginTop: 3, flexShrink: 0, width: 'auto', accentColor: '#006363' }} />
              <span>I’ll send the list of work activities later (our auditor will go through it with you on the audit day).</span>
            </label>
            {!later && rows.map((r, i) => (
              <div key={i} style={{ border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 10, padding: '12px 14px', marginBottom: 10, background: '#fcfcfb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Activity {i + 1}</span>
                  {rows.length > 1 && (
                    <button onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}
                      style={{ fontSize: 13, border: 'none', background: 'none', color: '#c0392b', cursor: 'pointer', textDecoration: 'underline' }}>Remove</button>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <Label required>Work activities</Label>
                  <textarea value={r.work_activities} onChange={e => setRow(i, 'work_activities', e.target.value)}
                    style={{ ...box, minHeight: 56, resize: 'vertical', ...(missing.rows && !rows.some(x => x.work_activities.trim()) ? bad : {}) }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                  <div><Label>Occupation</Label><input value={r.occupation} onChange={e => setRow(i, 'occupation', e.target.value)} style={box} /></div>
                  <div><Label>Department</Label><input value={r.department} onChange={e => setRow(i, 'department', e.target.value)} style={box} /></div>
                  <div><Label>No. of employees affected</Label><input type="number" min="0" value={r.no_affected} onWheel={e => e.currentTarget.blur()} onChange={e => setRow(i, 'no_affected', e.target.value)} style={box} /></div>
                </div>
              </div>
            ))}
            {!later && <button onClick={() => setRows(rs => [...rs, emptyRow()])}
              style={{ fontSize: 14, padding: '8px 14px', borderRadius: 8, border: '0.5px solid #006363', background: '#fff', color: '#006363', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 20 }}>
              + Add activity
            </button>}
          </div>

          {sectionTitle('MOM Annex A — work activities for which overtime exemption will not be granted')}
          <ol style={{ fontSize: 14, lineHeight: 1.6, paddingLeft: 22, margin: '0 0 8px' }}>
            {OTE_ANNEX_A.map((c, i) => <li key={i}>{c.title.replace(/[.:]?\s*Examples:$|:$/, '')}</li>)}
          </ol>
          <button onClick={() => setShowAnnex(s => !s)}
            style={{ fontSize: 13, border: 'none', background: 'none', color: '#006363', cursor: 'pointer', textDecoration: 'underline', padding: 0, marginBottom: 12 }}>
            {showAnnex ? 'Hide full list' : 'Read full list (MOM’s wording)'}
          </button>
          {showAnnex && (
            <div style={{ fontSize: 13, lineHeight: 1.6, color: '#333', background: '#f7f6f3', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>WORK ACTIVITIES FOR WHICH OVERTIME EXEMPTION WILL NOT BE GRANTED</div>
              {OTE_ANNEX_A.map((c, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div>{i + 1}) {c.title}</div>
                  {c.items?.length > 0 && <ul style={{ margin: '2px 0 0', paddingLeft: 22 }}>{c.items.map(x => <li key={x}>{x}</li>)}</ul>}
                  {c.groups?.map(g => (
                    <div key={g.head} style={{ marginLeft: 22, marginTop: 4 }}>
                      <div style={{ textDecoration: 'underline' }}>{g.head}</div>
                      <ul style={{ margin: '2px 0 0', paddingLeft: 22 }}>{g.items.map(x => <li key={x}>{x}</li>)}</ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          <label ref={el => { refs.current.declared = el }}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 20, padding: '12px 14px', borderRadius: 8,
              background: missing.declared ? '#fdf1f0' : '#f7f6f3', border: missing.declared ? '1px solid #c0392b' : '1px solid transparent' }}>
            <input type="checkbox" checked={declared} onChange={e => { setDeclared(e.target.checked); setMissing(m => ({ ...m, declared: false })) }}
              style={{ marginTop: 3, flexShrink: 0, width: 'auto', accentColor: '#006363' }} />
            <span style={{ fontSize: 14, color: '#333', lineHeight: 1.6 }}>{OTE_DECLARATION}<span style={{ color: '#c0392b', marginLeft: 3 }}>*</span></span>
          </label>

          {field('remarks', 'Any questions or notes for us', { textarea: true })}

          <button onClick={submit} disabled={submitting}
            style={{ marginTop: 8, width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none', background: '#006363', color: '#fff', fontWeight: 500, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}
